"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import CostSidebarWidget from "@/components/CostSidebarWidget";
import Icon from "@/components/Icon";
import type { ModuleId, NavModule } from "@/lib/permissions";
import type { ProjectNavItem } from "@/lib/projects-nav";

type NavItem = { label: string; href: string; icon?: string; disabled?: boolean; badge?: number };

type Props = {
  modules: NavModule[];
  projectItems: ProjectNavItem[];
  access: ModuleId[];
  badges?: Record<string, number>;
  currentUser?: { displayName: string; email: string; initials: string };
};

function ComingSoonTile() {
  return (
    <div
      className="group relative mb-2 cursor-help overflow-hidden rounded-lg border border-dashed border-sl-gold/40 bg-gradient-to-br from-sl-gold/10 to-transparent px-3 py-2.5 transition-colors hover:border-sl-gold/70 hover:from-sl-gold/15"
      title="Fully customizable project apps"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-sl-gold animate-pulse" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-sl-gold">Coming soon</span>
      </div>
      <div className="mt-0.5 max-h-0 overflow-hidden text-[10px] leading-snug text-slate-400 opacity-0 transition-all duration-200 group-hover:max-h-12 group-hover:opacity-100">
        Fully customizable project apps
      </div>
    </div>
  );
}

function NavList({ items }: { items: NavItem[] }) {
  const pathname = usePathname();
  return (
    <ul className="space-y-1.5">
      {items.map((it, ii) => {
        const active = !it.disabled && (pathname === it.href || (it.href !== "/" && pathname.startsWith(`${it.href}/`)));
        const cls = [
          "group flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
          active
            ? "bg-white/10 text-white ring-1 ring-white/10 shadow-sm"
            : it.disabled
            ? "text-slate-500 cursor-not-allowed"
            : "text-slate-300 hover:bg-white/[0.07] hover:text-white",
        ].join(" ");
        const icon = it.icon && <Icon name={it.icon} className={`h-4 w-4 ${active ? "text-sl-gold" : "text-slate-400 group-hover:text-slate-200"}`} />;
        const badge = it.badge && it.badge > 0
          ? <span className="ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-sl-accent px-1.5 text-[10px] font-semibold text-white">{it.badge}</span>
          : null;
        if (it.disabled) {
          return <li key={ii}><span className={cls}>{icon}<span className="truncate">{it.label}</span>{badge}</span></li>;
        }
        return <li key={ii}><Link href={it.href} className={cls}>{icon}<span className="truncate">{it.label}</span>{badge}</Link></li>;
      })}
    </ul>
  );
}

function Section({ label, children }: { label?: string; children: React.ReactNode }) {
  return (
    <div>
      {label && <div className="px-3 pb-2 text-[10px] font-semibold tracking-[0.2em] text-slate-500">{label}</div>}
      {children}
    </div>
  );
}

export default function SidebarClient({ modules, projectItems, access, badges, currentUser }: Props) {
  const accessSet = new Set(access);
  const b = badges ?? {};
  const main = modules.filter((m) => m.section === "main" && accessSet.has(m.id)).map((m) => ({ label: m.label, href: m.href, icon: m.icon, badge: b[m.id] }));
  const settings = modules.filter((m) => m.section === "settings" && accessSet.has(m.id)).map((m) => ({ label: m.label, href: m.href, icon: m.icon, badge: b[m.id] }));
  const projectsAllowed = accessSet.has("projects");

  return (
    <aside className="w-72 shrink-0 bg-sl-navy text-slate-200 flex flex-col h-screen sticky top-0 border-r border-black/20">
      <div className="px-5 pt-5 pb-5 border-b border-white/10">
        <div className="rounded-xl bg-white px-4 py-3 shadow-sm">
          <img src="/brand/scarborough-lane-logo.png" alt="Scarborough Lane Development" className="h-auto w-full object-contain" />
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-[0.24em] text-slate-500 text-center">Mission Control</div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-6 overflow-y-auto">
        <Section><NavList items={main} /></Section>

        {projectsAllowed && (
          <Section label="PROJECTS">
            <ComingSoonTile />
            <NavList items={projectItems.length ? projectItems.map((p) => ({ label: p.label, href: p.href, icon: p.icon })) : [{ label: "No SharePoint projects found", href: "#", disabled: true }]} />
          </Section>
        )}

        <Section label="SETTINGS"><NavList items={settings} /></Section>
      </nav>

      <div className="border-t border-white/10 p-4 space-y-3">
        <CostSidebarWidget />
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 ring-1 ring-white/5">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-sl-good opacity-60 animate-ping"></span><span className="relative inline-flex h-2 w-2 rounded-full bg-sl-good"></span></span>
          <span className="text-sm text-slate-200">Matt Ezra</span>
          <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-500">online</span>
        </div>
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="h-8 w-8 rounded-full bg-sl-gold/15 ring-1 ring-sl-gold/30 flex items-center justify-center text-xs font-semibold text-sl-gold">{currentUser?.initials ?? "?"}</div>
          <div className="leading-tight min-w-0 flex-1">
            <div className="text-sm text-white truncate">{currentUser?.displayName ?? "Unknown"}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider truncate">{currentUser?.email ?? ""}</div>
          </div>
          <a href="/api/auth/logout" title="Sign out" className="text-slate-500 hover:text-white transition" aria-label="Sign out">
            <Icon name="log-out" className="h-4 w-4" />
          </a>
        </div>
      </div>
    </aside>
  );
}
